import React, { useState } from 'react';
import {
  Box,
  Button,
  Paper,
  CircularProgress,
  Typography,
} from '@mui/material';
import { LocationOn, Work, MonetizationOn, InsertDriveFile } from '@mui/icons-material';
import { FormDatas } from '../types/FormData';
import LocationSelection from './steps/LocationSelection';
import RoleSelection from './steps/RoleSelection';
import FundingOption from './steps/FundingOption';
import DocumentUpload from './steps/DocumentUpload';
import KnowledgeGapTest from './steps/KnowledgeGapTest';
import ScheduleAppointment from './steps/ScheduleAppointment';
import ContactInformation from './steps/ContactInformation';
import Confirmation from './steps/Confirmation';
import { createInterest } from 'src/services/public.services';
import { useSnackbar } from 'src/contexts/SnackbarContext';

const MultiStepForm: React.FC = () => {
  const [step, setStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState<FormDatas>({
    location: '',
    locationId: 0,
    role: '',
    fundingOption: '',
    documents: [],
    takeKnowledgeTest: false,
    appointment: null,
    firstName: '',
    lastName: '',
    phoneNumber: '',
    email: '',
  });

  const { showMessage } = useSnackbar();

  // This submission function is passed into the ContactInformation step.
  // When the API call is successful, we move to the final Confirmation step (index 7).
  const submitForm = async () => {
    if (!validateCurrentStep()) return;

    setLoading(true);

    const payload = {
      ...formData,
      appointment: formData.appointment ? new Date(formData.appointment) : null,
      documents: formData.documents.map((doc) => ({
        name: doc.name,
        content: doc.content,
      })),
    };

    try {
      await createInterest(payload);
      showMessage('Formular erfolgreich abgeschickt!', 'success');
      // Move to the final Confirmation step (index 7)
      setStep(7);
    } catch (error: any) {
      showMessage(
        error.message || 'Fehler beim Absenden. Bitte versuchen Sie es erneut.',
        'error'
      );
    } finally {
      setLoading(false);
    }
  };

  // Simple email validator.
  const validateEmail = (email: string): boolean => {
    const regex = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,4}$/;
    return regex.test(email);
  };

  // Validate fields for the current step.
  const validateCurrentStep = (): boolean => {
    switch (step) {
      case 0:
        if (!formData.location) {
          showMessage('Bitte wählen Sie einen Standort aus.', 'error');
          return false;
        }
        break;
      case 1:
        if (!formData.role) {
          showMessage('Bitte wählen Sie eine Rolle aus.', 'error');
          return false;
        }
        break;
      case 2:
        if (!formData.fundingOption) {
          showMessage('Bitte wählen Sie eine Finanzierungsoption aus.', 'error');
          return false;
        }
        break;
      // For the ContactInformation step (7th step, index 6)
      case 6:
        if (
          !formData.firstName ||
          !formData.lastName ||
          !formData.phoneNumber ||
          !formData.email
        ) {
          showMessage(
            'Bitte füllen Sie alle Felder für die Kontaktinformationen aus.',
            'error'
          );
          return false;
        }
        if (!validateEmail(formData.email)) {
          showMessage('Bitte geben Sie eine gültige E-Mail-Adresse ein.', 'error');
          return false;
        }
        break;
      default:
        break;
    }
    return true;
  };

  // Navigation: nextStep moves to the next step (with conditional skipping).
  const nextStep = () => {
    if (!validateCurrentStep()) return;

    // Skip DocumentUpload if fundingOption is "Self-payer".
    if (step === 2 && formData.fundingOption === 'Self-payer') {
      setStep(step + 2);
    }
    // Skip KnowledgeGapTest (and its following step) if the test is not taken.
    else if (step === 4 && !formData.takeKnowledgeTest) {
      setStep(step + 2);
    } else {
      setStep(step + 1);
    }
  };

  // Navigation: prevStep moves back to the previous step.
  const prevStep = () => {
    if (step === 6 && !formData.takeKnowledgeTest) {
      setStep(4);
    } else if (step === 4 && formData.fundingOption === 'Self-payer') {
      setStep(2);
    } else {
      setStep(step - 1);
    }
  };

  // Define all steps of the form (8 steps total):
  // 0: Standortauswahl  
  // 1: Rollenwahl  
  // 2: Wie wird die Nachhilfe finanziert?  
  // 3: Dokumenten-Upload  
  // 4: Wissenslückentest  
  // 5: Terminvereinbarung  
  // 6: Kontaktinformationen  
  // 7: Bestätigung
  const steps = [
    {
      label: 'Standortauswahl',
      component: <LocationSelection formData={formData} setFormData={setFormData} />,
      icon: <LocationOn />,
    },
    {
      label: 'Rollenwahl',
      component: <RoleSelection formData={formData} setFormData={setFormData} />,
      icon: <Work />,
    },
    {
      label: 'Wie wird die Nachhilfe finanziert?',
      component: <FundingOption formData={formData} setFormData={setFormData} />,
      icon: <MonetizationOn />,
    },
    {
      label: 'Dokumenten-Upload',
      component:
        formData.fundingOption === 'Education and Participation'
          ? <DocumentUpload formData={formData} setFormData={setFormData} />
          : null,
      icon: <InsertDriveFile />,
    },
    {
      label: 'Wissenslückentest',
      component: <KnowledgeGapTest formData={formData} setFormData={setFormData} />,
      icon: <InsertDriveFile />,
    },
    {
      label: 'Terminvereinbarung',
      component: <ScheduleAppointment formData={formData} setFormData={setFormData} />,
      icon: <InsertDriveFile />,
    },
    {
      label: 'Kontaktinformationen',
      // Here we pass the onSubmit callback so that clicking “einreichen” triggers submitForm.
      component: (
        <ContactInformation
          formData={formData}
          setFormData={setFormData}
          onSubmit={submitForm}
        />
      ),
      icon: <InsertDriveFile />,
    },
    {
      label: 'Bestätigung',
      component: <Confirmation formData={formData} />,
      icon: <InsertDriveFile />,
    },
  ];

  return (
    <Box sx={{ width: '100%', overflowX: 'auto', padding: { xs: 1, sm: 3 } }}>
      <Paper elevation={3} sx={{ p: 3, borderRadius: 3 }}>
        {/* Header with current step label and step counter */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            {steps[step].icon}
            <Typography variant="h6" sx={{ ml: 1 }}>
              {steps[step].label}
            </Typography>
          </Box>
          <Typography variant="body2" color="text.secondary">
            {step + 1} / {steps.length}
          </Typography>
        </Box>

        <Box sx={{ mt: 4 }}>{steps[step].component}</Box>

        {/* Navigation Buttons:
            - For steps 0 to 5 (indexes 0 to steps.length-3): show both Prev and Next.
            - For step 6 (Kontaktinformationen): show only the "Vorherige" button.
            - For step 7 (Bestätigung): show no navigation buttons. */}
        {step < steps.length - 2 ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
            {step > 0 && (
              <Button onClick={prevStep} variant="outlined" sx={{ mr: 2 }}>
                Vorherige
              </Button>
            )}
            <Button variant="contained" onClick={nextStep}>
              Weiter
            </Button>
          </Box>
        ) : step === steps.length - 2 ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
            {step > 0 && (
              <Button onClick={prevStep} variant="outlined">
                Vorherige
              </Button>
            )}
          </Box>
        ) : null}

        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
            <CircularProgress />
          </Box>
        )}
      </Paper>
    </Box>
  );
};

export default MultiStepForm;
